import React, { FC, useState, useEffect, useRef } from 'react';
import { Page, StyledContainer, Flexbox } from '@src/views/containers';
import { CorvalProjectAccount, CorvalProjectVersion, FlexAlign, FlexJustify, FlexFlow } from '@src/types';
import { ChatMessage, useAiContext } from '@src/views/pages/projects/dash/AiContext';
import {
  AIContextPills,
} from '@src/views/pages/projects/dash/DashAIPanel';
import AIChatBox from '@src/views/pages/projects/dash/AIChatBox';
import AIObjectPicker, { SelectedItem } from '@src/views/pages/projects/dash/AIObjectPicker';
import { Icon, Button, Header, Menu, Modal } from 'semantic-ui-react';
import { useCurrentUser, usePrefStates } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';

// @ts-ignore
import sparkleSvg from '@src/_assets/images/sparkle.svg';
import '../projects/dash/Dash.scss';

const SCOPED_AI_OBJECT_TYPES: SelectedItem['objectType'][] = ['Project_Id', 'Project_Version_Id'];

interface ApiConversationMessage {
  id: string;
  sequenceNumber?: number;
  userPrompt: string;
  aiResponse?: string;
  createdUtc: string;
}

interface ApiConversation {
  conversationId: string;
  threadId?: string;
  initialQuestion: string;
  indexes?: string[];
  variables?: Record<string, string[]>;
  datasetJson?: string;
  reasoning?: string;
  createdUtc: string;
  updatedUtc: string;
  messages?: ApiConversationMessage[];
}

interface DatasetPayload {
  indexName: string;
  documents: any[];
}

interface ConversationTab {
  id: string;
  agentKey: string;
  title: string;
  threadId: string | null;
  indexes: string[];
  variables: Record<string, string[]>;
  datasetPayload: DatasetPayload[];
  updatedUtc: string;
  isTemporary?: boolean;
}

const sanitizeAssistantMessageText = (text: string): string => {
  const lines = text.split('\n');
  const kept: string[] = [];
  let skippingMetadataBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const startsMetadataBlock = /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

    if (startsMetadataBlock) {
      skippingMetadataBlock = true;
      continue;
    }

    if (skippingMetadataBlock) {
      const isContinuation =
        trimmed.length === 0 ||
        /^[-*]/.test(trimmed) ||
        /^[\[{\"]/.test(trimmed) ||
        /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

      if (isContinuation) {
        continue;
      }

      skippingMetadataBlock = false;
    }

    kept.push(line);
  }

  return kept.join('\n').trimEnd();
};

function isRichAiContextItems(items: SelectedItem[]): boolean {
  const allowed = new Set<string>(SCOPED_AI_OBJECT_TYPES);
  return items.some((i) => !allowed.has(i.objectType));
}

function areAiSelectionsEqualByIds(a: SelectedItem[], b: SelectedItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].objectType !== b[i].objectType || a[i].objectId !== b[i].objectId) {
      return false;
    }
  }
  return true;
}

function normalizePrefProjectId(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '' || raw === 0) {
    return '';
  }
  const str = String(raw).trim();
  if (str === '0') {
    return '';
  }
  return str;
}

function findVersionLabel(projectAccount: CorvalProjectAccount, versionId: string): string {
  const versions = (projectAccount.project?.projectVersions ?? []).filter(Boolean) as CorvalProjectVersion[];
  const match = versions.find((v) => v.id === versionId);
  return match?.name?.trim() ? match.name : 'Version';
}

function normalizePrefVersionId(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  return String(raw);
}

function buildProjectScopeItems(account: CorvalProjectAccount, versionId: string): SelectedItem[] {
  const projectId = account.projectId;
  const projectName = account.project?.name?.trim() ? account.project.name : 'Project';
  const scopeItems: SelectedItem[] = [
    { objectType: 'Project_Id', objectId: projectId, name: projectName },
  ];

  if (versionId) {
    scopeItems.push({
      objectType: 'Project_Version_Id',
      objectId: versionId,
      name: findVersionLabel(account, versionId),
    });
  }

  return scopeItems;
}

function mergeProjectScopeIntoSelection(prev: SelectedItem[], scopeItems: SelectedItem[]): SelectedItem[] {
  if (!isRichAiContextItems(prev)) {
    return scopeItems;
  }

  const withoutScope = prev.filter(
    (item) => item.objectType !== 'Project_Id' && item.objectType !== 'Project_Version_Id'
  );

  return [...scopeItems, ...withoutScope];
}

function areProjectScopeItemsEqual(a: SelectedItem[], b: SelectedItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < b.length; i += 1) {
    const match = a.find((item) => item.objectType === b[i].objectType && item.objectId === b[i].objectId);
    if (!match || match.name !== b[i].name) {
      return false;
    }
  }

  return true;
}

const DoraAIAssistantPageContent: FC = () => {
  const {
    selectedObjects = [],
    setSelectedObjects,
    updateAgent,
    getAgent,
  } = useAiContext();
  const { GetAIConversations } = useCentralCalls();
  const { currentUser } = useCurrentUser();
  const [, prefs, prefEnums] = usePrefStates();
  const prefProjectIdKey = normalizePrefProjectId(prefs.get(prefEnums.PROJECT_ID));
  const [, projectSpecificPrefs] = usePrefStates(prefProjectIdKey);
  const projects = currentUser?.projects;
  const [ conversationTabs, setConversationTabs ]             = useState<ConversationTab[]>( [] ),
        [ activeConversationId, setActiveConversationId ]     = useState<string | null>( null ),
        [ isLoadingConversations, setIsLoadingConversations ] = useState( false ),
        [ datasetModalOpen, setDatasetModalOpen ]             = useState( false ),
        [ pickerOpen, setPickerOpen ]                         = useState( false ),
        lastSyncedScopeRef                                    = useRef<string | null>( null ),
        [ prefsRevision, setPrefsRevision ]                   = useState(0);

  const prefProjectVersionId = normalizePrefVersionId(
    projectSpecificPrefs.get(prefEnums.PROJECT_VERSION_ID)
  );
  const scopeSyncKey = `${prefProjectIdKey}|${prefProjectVersionId}`;

  useEffect(() => {
    const handlePrefsUpdated = () => setPrefsRevision((revision) => revision + 1);

    window.addEventListener('storage', handlePrefsUpdated);

    return () => window.removeEventListener('storage', handlePrefsUpdated);
  }, []);

  useEffect(() => {
    if (!projects) {
      return;
    }

    if (lastSyncedScopeRef.current === scopeSyncKey) {
      return;
    }

    const account = projects.find((p) => p.projectId === prefProjectIdKey);

    if (!account?.project?.id) {
      lastSyncedScopeRef.current = scopeSyncKey;

      setSelectedObjects((prev) => {
        if (isRichAiContextItems(prev)) {
          return prev;
        }
        return prev.length > 0 ? [] : prev;
      });

      return;
    }

    const scopeItems = buildProjectScopeItems(account, prefProjectVersionId);

    setSelectedObjects((prev) => {
      const next = mergeProjectScopeIntoSelection(prev, scopeItems);
      const prevScope = prev.filter(
        (item) => item.objectType === 'Project_Id' || item.objectType === 'Project_Version_Id'
      );

      if (areAiSelectionsEqualByIds(prev, next) && areProjectScopeItemsEqual(prevScope, scopeItems)) {
        return prev;
      }

      return next;
    });

    lastSyncedScopeRef.current = scopeSyncKey;
  }, [prefProjectIdKey, prefProjectVersionId, projects, prefsRevision, scopeSyncKey, setSelectedObjects]);

  const buildDatasetMessageText = (conversation: ApiConversation): string => {
    const indexes = Array.isArray(conversation.indexes) ? conversation.indexes : [];
    const variables = conversation.variables ?? {};
    const variablePairs = Object.entries(variables);

    const lines: string[] = ['### Dataset'];
    lines.push(indexes.length > 0 ? `**Indexes:** ${indexes.join(', ')}` : '**Indexes:** None');

    if (variablePairs.length > 0) {
      lines.push('**Variables:**');
      variablePairs.forEach(([key, values]) => {
        lines.push(`- ${key}: ${(values ?? []).join(', ')}`);
      });
    } else {
      lines.push('**Variables:** None');
    }

    return lines.join('\n');
  };

  const buildMessagesForConversation = (conversation: ApiConversation): ChatMessage[] => {
    const baseTimestamp = new Date(conversation.createdUtc || conversation.updatedUtc || Date.now());
    const messages: ChatMessage[] = [
      {
        id: `dataset-${conversation.conversationId}`,
        text: buildDatasetMessageText(conversation),
        isUser: false,
        timestamp: baseTimestamp,
      },
    ];

    const turns = Array.isArray(conversation.messages) ? conversation.messages : [];
    turns.forEach((turn, idx) => {
      const turnTimestamp = new Date(turn.createdUtc || baseTimestamp);
      if (turn.userPrompt && turn.userPrompt.trim()) {
        messages.push({
          id: `user-${conversation.conversationId}-${turn.id || idx}`,
          text: turn.userPrompt,
          isUser: true,
          timestamp: turnTimestamp,
        });
      }

      if (turn.aiResponse && turn.aiResponse.trim()) {
        messages.push({
          id: `assistant-${conversation.conversationId}-${turn.id || idx}`,
          text: sanitizeAssistantMessageText(turn.aiResponse),
          isUser: false,
          timestamp: turnTimestamp,
          debug: {
            source: 'historical-conversation',
            conversationId: conversation.conversationId,
            threadId: conversation.threadId ?? null,
            sequenceNumber: turn.sequenceNumber ?? null,
            createdUtc: turn.createdUtc,
            indexes: Array.isArray(conversation.indexes) ? conversation.indexes : [],
            variables: conversation.variables ?? {},
            reasoning: conversation.reasoning ?? null,
          },
        });
      }
    });

    return messages;
  };

  const getConversationTitle = (conversation: ApiConversation): string => {
    const question = conversation.initialQuestion?.trim() ?? '';
    if (question.length === 0) {
      return 'Conversation';
    }

    return question.length > 48 ? `${question.slice(0, 48)}...` : question;
  };

  const createTemporaryConversationTab = (): ConversationTab => {
    const id = `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const agentKey = `conversation:${id}`;
    updateAgent(agentKey, {
      messages: [],
      threadId: null,
      isLoading: false,
    });

    return {
      id,
      agentKey,
      title: 'New Conversation',
      threadId: null,
      indexes: [],
      variables: {},
      datasetPayload: [],
      updatedUtc: new Date().toISOString(),
      isTemporary: true,
    };
  };

  const parseDatasetPayload = (datasetJson?: string): DatasetPayload[] => {
    if (!datasetJson || !datasetJson.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(datasetJson);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item: any) => {
          const indexName =
            (typeof item?.IndexName === 'string' && item.IndexName.trim()) ||
            (typeof item?.indexName === 'string' && item.indexName.trim()) ||
            'index';

          const docs = Array.isArray(item?.Documents)
            ? item.Documents
            : Array.isArray(item?.documents)
              ? item.documents
              : [];

          return {
            indexName,
            documents: docs,
          } as DatasetPayload;
        })
        .filter((payload: DatasetPayload) => payload.documents.length > 0);
    } catch {
      return [];
    }
  };

  const renderJsonValue = (value: any, depth: number = 0, label?: string): React.ReactNode => {
    const indentStyle = depth > 0 ? { marginLeft: depth * 12 } : undefined;

    if (value === null || value === undefined) {
      return (
        <StyledContainer className="ai-json-primitive" style={indentStyle}>
          {label ? <span className="ai-json-label">{label}:</span> : null}{' '}
          {String(value)}
        </StyledContainer>
      );
    }

    if (Array.isArray(value)) {
      const header = label ? `${label} [${value.length}]` : `Array [${value.length}]`;
      return (
        <StyledContainer className="ai-json-block" style={indentStyle}>
          <details>
            <summary className="ai-json-summary">{header}</summary>
            <StyledContainer className="ai-json-content">
              {value.map((item, index) => (
                <StyledContainer key={`arr-${depth}-${index}`} className="ai-json-item">
                  {renderJsonValue(item, depth + 1, `[${index}]`)}
                </StyledContainer>
              ))}
            </StyledContainer>
          </details>
        </StyledContainer>
      );
    }

    if (typeof value === 'object') {
      const header = label ?? 'Object';
      return (
        <StyledContainer className="ai-json-block" style={indentStyle}>
          <details>
            <summary className="ai-json-summary">{header}</summary>
            <StyledContainer className="ai-json-content">
              {Object.entries(value).map(([key, val]) => (
                <StyledContainer key={`obj-${depth}-${key}`} className="ai-json-item">
                  {renderJsonValue(val, depth + 1, key)}
                </StyledContainer>
              ))}
            </StyledContainer>
          </details>
        </StyledContainer>
      );
    }

    return (
      <StyledContainer className="ai-json-prewrap" style={indentStyle}>
        {label ? <span className="ai-json-label">{label}:</span> : null}{' '}
        {String(value)}
      </StyledContainer>
    );
  };

  const loadConversations = async (preferredConversationId?: string) => {
    setIsLoadingConversations(true);
    try {
      const data = (await GetAIConversations()) as ApiConversation[];
      const sorted = [...data].sort(
        (a, b) => new Date(b.updatedUtc).getTime() - new Date(a.updatedUtc).getTime()
      );

      const tabs: ConversationTab[] = sorted.map((conversation) => {
        const agentKey = `conversation:${conversation.conversationId}`;
        updateAgent(agentKey, {
          messages: buildMessagesForConversation(conversation),
          threadId: conversation.threadId ?? null,
          isLoading: false,
        });

        return {
          id: conversation.conversationId,
          agentKey,
          title: getConversationTitle(conversation),
          threadId: conversation.threadId ?? null,
          indexes: Array.isArray(conversation.indexes) ? conversation.indexes : [],
          variables: conversation.variables ?? {},
          datasetPayload: parseDatasetPayload(conversation.datasetJson),
          updatedUtc: conversation.updatedUtc,
        };
      });

      if (tabs.length === 0) {
        const newTab = createTemporaryConversationTab();
        setConversationTabs([newTab]);
        setActiveConversationId(newTab.id);
        return;
      }

      setConversationTabs(tabs);

      if (preferredConversationId && tabs.some((tab) => tab.id === preferredConversationId)) {
        setActiveConversationId(preferredConversationId);
      } else if (activeConversationId && tabs.some((tab) => tab.id === activeConversationId)) {
        setActiveConversationId(activeConversationId);
      } else {
        setActiveConversationId(tabs[0].id);
      }
    } catch (error) {
      console.error('Failed to load AI conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createConversationTab = () => {
    const newTab = createTemporaryConversationTab();
    setConversationTabs((prev) => [newTab, ...prev]);
    setActiveConversationId(newTab.id);
  };

  const handleAssistantMessage = async (msg: ChatMessage) => {
    if (!msg.conversationId) {
      return;
    }

    const applyRuntimeDebugToLatestAssistant = (conversationId: string, runtimeDebug: any) => {
      if (!runtimeDebug) {
        return;
      }

      const agentKey = `conversation:${conversationId}`;
      const state = getAgent(agentKey);
      const currentMessages = state?.messages ?? [];
      if (currentMessages.length === 0) {
        return;
      }

      const latestAssistantIndex = [...currentMessages]
        .map((message, index) => ({ message, index }))
        .reverse()
        .find((entry) => !entry.message.isUser && !entry.message.isHeader)?.index;

      if (latestAssistantIndex === undefined) {
        return;
      }

      const nextMessages = currentMessages.map((message, index) => {
        if (index !== latestAssistantIndex) {
          return message;
        }

        return {
          ...message,
          debug: {
            ...(message.debug ?? {}),
            ...runtimeDebug,
            source: 'historical+runtime',
          },
        };
      });

      updateAgent(agentKey, { messages: nextMessages });
    };

    const startedNewConversation = msg.debug?.startedNewConversation === true;
    const switchedConversationId =
      !!activeConversationId &&
      msg.conversationId !== activeConversationId;

    if (startedNewConversation || switchedConversationId) {
      await loadConversations(msg.conversationId);
      applyRuntimeDebugToLatestAssistant(msg.conversationId, msg.debug);
      return;
    }

    const activeTab = conversationTabs.find((tab) => tab.id === activeConversationId) ?? null;
    if (activeTab?.isTemporary) {
      await loadConversations(msg.conversationId);
      return;
    }

    setConversationTabs((prev) =>
      prev.map((tab) =>
        tab.id === msg.conversationId
          ? {
              ...tab,
              updatedUtc: new Date().toISOString(),
            }
          : tab
      )
    );
  };

  const highlightedTypes = new Set(selectedObjects.map((o) => o.objectType));
  const activeConversation = conversationTabs.find((tab) => tab.id === activeConversationId) ?? null;
  const datasetRecordCount = activeConversation?.datasetPayload.reduce((sum, payload) => sum + payload.documents.length, 0) ?? 0;

  const removeSelectedObject = (objectId: string) =>
    setSelectedObjects((prev) => prev.filter((o) => o.objectId !== objectId));

  const handlePickerClose = (selected: SelectedItem[]) => {
    setSelectedObjects(selected);
    setPickerOpen(false);
  };

  return (
    <Page
      showCorvalAssistant={false}
      padMainHorizontal={false}
      padMainVertical={false}
      allowContentScroll
      showToggleMode={false}
    >
      <Flexbox className="ai-assistant-page" align={FlexAlign.STRETCH} justify={FlexJustify.START}>

        <StyledContainer className="ai-assistant-sidebar">
          <Header as="h3" className="ai-assistant-sidebar-title">
            <img src={sparkleSvg} alt="AI Assistant" />
            <Header.Content>
              AI Assistant
              <Header.Subheader>Your Project Assistant</Header.Subheader>
            </Header.Content>
          </Header>

          <Button
            fluid
            color="purple"
            icon="plus"
            content="New Conversation"
            onClick={createConversationTab}
            className="ai-assistant-new-btn"
          />

          {isLoadingConversations && (
            <StyledContainer className="ai-assistant-loading">
              Loading conversations...
            </StyledContainer>
          )}

          <Menu
            vertical
            fluid
            secondary
            text
            className="ai-assistant-conversation-menu"
          >
            {conversationTabs.map((tab) => (
              <Menu.Item
                key={tab.id}
                active={activeConversationId === tab.id}
                onClick={() => setActiveConversationId(tab.id)}
                className="ai-assistant-conversation-item"
              >
                <Icon
                  name={tab.isTemporary ? 'edit outline' : 'comments outline'}
                  className="ai-assistant-conversation-icon"
                />
                <Flexbox direction={FlexFlow.COLS} className="ai-assistant-conversation-meta">
                  <span>{tab.title}</span>
                  <span className="ai-assistant-conversation-time">
                    {new Date(tab.updatedUtc).toLocaleString()}
                  </span>
                </Flexbox>
              </Menu.Item>
            ))}
          </Menu>
        </StyledContainer>

        <StyledContainer className="ai-assistant-content">

          <Flexbox
            align={FlexAlign.CENTER}
            justify={FlexJustify.SPACE_BETWEEN}
            className="ai-assistant-header-bar"
          >
            <Header as="h4" className="ai-assistant-header-title">
              {activeConversation?.title ?? 'Conversation'}
              <Header.Subheader>
                {selectedObjects.length > 0
                  ? `Focusing on ${selectedObjects.length} item${selectedObjects.length !== 1 ? 's' : ''}`
                  : 'Select items for focused analysis'}
              </Header.Subheader>
            </Header>

            <Button
              size="tiny"
              basic
              icon="add"
              content="Select Objects"
              onClick={() => setPickerOpen(true)}
            />
          </Flexbox>

          {activeConversation && activeConversation.indexes.length > 0 && (
            <StyledContainer className="ai-assistant-dataset-btn-wrap">
              <Button
                basic
                size="small"
                icon="database"
                content={`Dataset: ${activeConversation.indexes.join(', ')} (${datasetRecordCount} records)`}
                onClick={() => setDatasetModalOpen(true)}
              />
            </StyledContainer>
          )}

          {selectedObjects.length > 0 && (
            <StyledContainer className="ai-assistant-pills">
              <AIContextPills
                selectedObjects={selectedObjects}
                highlightedTypes={highlightedTypes}
                onRemove={removeSelectedObject}
              />
            </StyledContainer>
          )}

          <StyledContainer className="ai-assistant-chat-area">
            {activeConversation && (
              <AIChatBox
                key={activeConversation.agentKey}
                agentType={activeConversation.agentKey}
                onAssistantMessage={handleAssistantMessage}
              />
            )}
          </StyledContainer>

        </StyledContainer>
      </Flexbox>

      <AIObjectPicker open={pickerOpen} onClose={handlePickerClose} />

      <Modal
        open={datasetModalOpen}
        onClose={() => setDatasetModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          Dataset Viewer
        </Modal.Header>
        <Modal.Content scrolling>
          {!activeConversation || activeConversation.datasetPayload.length === 0 ? (
            <StyledContainer className="ai-assistant-dataset-empty">No dataset available for this conversation.</StyledContainer>
          ) : (
            <StyledContainer>
              {activeConversation.datasetPayload.map((payload, idx) => (
                <StyledContainer key={`${payload.indexName}-${idx}`} className="ai-assistant-dataset-group">
                  <StyledContainer className="ai-assistant-dataset-group-title">
                    {payload.indexName} ({payload.documents.length} records)
                  </StyledContainer>
                  {payload.documents.map((doc, docIndex) => (
                    <StyledContainer
                      key={`${payload.indexName}-${idx}-${docIndex}`}
                      className="ai-assistant-dataset-card ai-json-tree"
                    >
                      {renderJsonValue(doc)}
                    </StyledContainer>
                  ))}
                </StyledContainer>
              ))}
            </StyledContainer>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDatasetModalOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </Page>
  );
};

const DoraAIAssistantPage: FC = () => {
  return <DoraAIAssistantPageContent />;
};

export default DoraAIAssistantPage;
